import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Calendar,
  Clock, ChevronRight, BookOpen, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import Header from './Header';
import Footer from './Footer';
import { blogPostsData, blogCategories } from '../data/blogData';
import { useAppointment } from '../context/AppointmentContext';
import usePageSeo from '../hooks/usePageSeo';

const Blog = () => {
  usePageSeo({
    title: "Regenerative Medicine Blog | ATL Regen Atlanta",
    description: "Read ATL Regen's expert blog on PRP therapy, regenerative medicine, pain management, and non-surgical treatments in Atlanta, GA.",
    canonical: "https://www.atlregen.com/blog",
    ogTitle: "ATL Regen Blog | Regenerative Medicine & PRP Insights",
  });
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const POSTS_PER_PAGE = 6;
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);
  const { openAppointmentModal } = useAppointment();

  const sortedPosts = useMemo(() =>
    [...blogPostsData].sort((a, b) => new Date(b.date) - new Date(a.date)),
  []);

  const filteredPosts = sortedPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const listPosts = filteredPosts;

  useEffect(() => {
    setVisibleCount(POSTS_PER_PAGE);
  }, [selectedCategory, searchQuery]);

  const visiblePosts = listPosts.slice(0, visibleCount);
  const hasMorePosts = visibleCount < listPosts.length;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B1B3A] via-[#2D2D4A] to-[#1B1B3A] py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#E63946] rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#20B2AA] rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white px-5 py-2 rounded-full text-sm font-semibold mb-6">
                <BookOpen className="h-4 w-4" />
                <span>ATLREGEN Blog</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Health & Wellness <span className="text-[#20B2AA]">Insights</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Expert articles on regenerative medicine, pain management, and living your best life
              </p>
              
              {/* Search Bar */}
              <div className="max-w-xl mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg rounded-full border-none bg-white shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>


        {/* Main Content */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-4 gap-12">
              {/* Sidebar */}
              <div className="lg:col-span-1 order-1 lg:order-1">
                <div className="sticky top-32">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Categories</h3>

                  {/* Mobile: dropdown */}
                  <div className="lg:hidden relative">
                    <button
                      onClick={() => setDropdownOpen(prev => !prev)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all"
                    >
                      <span className="font-medium">{selectedCategory}</span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                          dropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {dropdownOpen && (
                      <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                        {blogCategories.map((category) => (
                          <button
                            key={category.name}
                            onClick={() => {
                              setSelectedCategory(category.name);
                              setDropdownOpen(false);
                            }}
                            className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors ${
                              selectedCategory === category.name
                                ? 'bg-[#E63946] text-white font-semibold'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span>{category.name}</span>
                            <span className={`text-xs ${
                              selectedCategory === category.name ? 'text-white/80' : 'text-gray-400'
                            }`}>
                              {category.count}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Desktop: full list */}
                  <div className="hidden lg:block space-y-2">
                    {blogCategories.map((category) => (
                      <button
                        key={category.name}
                        onClick={() => setSelectedCategory(category.name)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                          selectedCategory === category.name
                            ? 'bg-[#E63946] text-white'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="font-medium">{category.name}</span>
                        <span className={`text-sm ${
                          selectedCategory === category.name ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          {category.count}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Newsletter Signup */}
                  <div className="mt-8 bg-gradient-to-br from-[#1B1B3A] to-[#2D2D4A] rounded-2xl p-6 text-white">
                    <h3 className="font-bold text-lg mb-2">Stay Updated</h3>
                    <p className="text-sm text-gray-300 mb-4">
                      Get the latest health insights delivered to your inbox.
                    </p>
                    <Input
                      type="email"
                      placeholder="Your email"
                      className="mb-3 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    />
                    <Button className="w-full bg-[#E63946] hover:bg-[#D32F3D]">
                      Subscribe
                    </Button>
                  </div>
                </div>
              </div>

              {/* Blog Posts Grid */}
              <div className="lg:col-span-3 order-2 lg:order-2">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedCategory === "All" ? "All Articles" : selectedCategory}
                  </h2>
                  <span className="text-gray-500">
                    {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {filteredPosts.length === 0 ? (
                  <div className="text-center py-16">
                    <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No articles found</h3>
                    <p className="text-gray-600">Try adjusting your search or category filter</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
                    {visiblePosts.map((post) => (
                      <Card 
                        key={post.id} 
                        className="group overflow-hidden border-gray-200 hover:border-[#E63946] hover:shadow-xl transition-all"
                      >
                        <Link to={`/blog/${post.slug}`}>
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={post.image}
                              alt={post.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute top-4 left-4">
                              <span className="bg-white/90 backdrop-blur-sm text-[#1B1B3A] text-xs font-semibold px-3 py-1 rounded-full">
                                {post.category}
                              </span>
                            </div>
                          </div>
                        </Link>
                        <CardContent className="p-5 md:p-6">
                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {post.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {post.readTime}
                            </span>
                          </div>
                          <Link to={`/blog/${post.slug}`}>
                            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-[#E63946] transition-colors line-clamp-2">
                              {post.title}
                            </h3>
                          </Link>
                          <p className="text-gray-600 mb-4 line-clamp-2 text-sm md:text-base">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                              <div className="bg-[#20B2AA] text-white w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold">
                                {post.author.split(' ').filter(n => !n.endsWith('.')).map(n => n[0]).join('')}
                              </div>
                              <span className="text-sm text-gray-600">{post.author}</span>
                            </div>
                            <Link to={`/blog/${post.slug}`} className="text-[#E63946] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                              Read More
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Load More */}
                {hasMorePosts && (
                  <div className="text-center mt-12">
                    <Button 
                      variant="outline" 
                      size="lg"
                      className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white px-8"
                      onClick={() => setVisibleCount(listPosts.length)}
                      data-testid="blog-load-more-btn"
                    >
                      Load More Articles
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Have Questions About Your Condition?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Our team is here to help. Book an appointment to discuss your treatment options.
              </p>
              <Button 
                size="lg" 
                className="bg-[#E63946] hover:bg-[#D32F3D] text-white px-8 py-6 text-lg font-bold"
                onClick={openAppointmentModal}
                data-testid="blog-schedule-btn"
              >
                Book an Appointment
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;